'use client';

import { PopoverContentProps } from '@radix-ui/react-popover';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronsUpDownIcon } from 'lucide-react';
import * as React from 'react';
import { ShortcutKbd } from './Shortcut';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Separator } from './separator';

type Item<T extends string = string> = {
  value: T;
  label: string | React.ReactNode;
  keywords?: string[];
  description?: string | React.ReactNode;
  onSelect?: (value: T) => void;
  isSelected?: boolean;
  leftElement?: React.ReactElement;
  rightElementOnFocus?: React.ReactElement;
};

type SelectedItem = Pick<Item, 'value' | 'label'> & { groupIndex: number };

type Group<T extends string = string> = {
  title?: string;
  items: Array<Item<T>>;
  onSelect?: (value: T) => void;
  forceMount?: boolean;
  emptyContent?: string;
};

export type ComboboxProps = {
  className?: string;
  Trigger?: React.ReactElement;
  placeholder?: string;
  groups:
    | Group[]
    | {
        tabs: Array<{
          title: string;
          groups: Group[];
        }>;
      };
  nothingFound?: React.ReactNode;
  showNothingFound?: boolean;
  side?: PopoverContentProps['side'];
  align?: PopoverContentProps['align'];
  onSelect?: (item: SelectedItem) => void;
  closeOnSelect?: boolean;
  highlightIfSelection?: boolean;
  /**
   * Should be used along with `disableInternalState` to manually set the selected value.
   */
  externalSelected?: React.ReactNode;
  disableInternalState?: boolean;
  triggerProps?: {
    wobble?: boolean;
    className?: string;
    invalid?: boolean;
  };
  commandListProps?: React.ComponentPropsWithoutRef<typeof CommandList>;
  controlled?: {
    open?: {
      open: boolean;
      setOpen: React.Dispatch<React.SetStateAction<boolean>>;
      shortcut?: string;
    };
    selection?: {
      selected: SelectedItem | null;
      setSelected: React.Dispatch<React.SetStateAction<SelectedItem | null>>;
    };
    search?: {
      search: string;
      setSearch: React.Dispatch<React.SetStateAction<string>>;
    };
  };
  commandInputRightAddon?: React.ReactNode;
  separatorBetweenGroups?: boolean;
  loop?: boolean;
};

export function Combobox({
  className,
  Trigger,
  placeholder = 'Select',
  groups,
  nothingFound = 'Nothing found ¯\\_(ツ)_/¯',
  showNothingFound = true,
  side,
  align,
  onSelect,
  closeOnSelect = true,
  highlightIfSelection = false,
  externalSelected,
  disableInternalState = false,
  triggerProps,
  commandListProps,
  controlled,
  commandInputRightAddon,
  separatorBetweenGroups = true,
  loop = false,
}: ComboboxProps) {
  const [__open, __setOpen] = React.useState(false);
  const [open, setOpen] = controlled?.open
    ? [controlled.open.open, controlled.open.setOpen]
    : [__open, __setOpen];

  const [__selected, __setSelected] = React.useState<SelectedItem | null>(null);
  const [selected, setSelected] = controlled?.selection
    ? [controlled.selection.selected, controlled.selection.setSelected]
    : [__selected, __setSelected];

  const isSelectedItem = (v: string, groupIndex: number) => {
    return selected?.value === v && selected?.groupIndex === groupIndex;
  };

  const renderedValue: React.ReactNode = (() => {
    if (disableInternalState) {
      return externalSelected || placeholder;
    } else {
      return selected?.label || placeholder;
    }
  })();

  const [selectedGroupIndex, setSelectedGroupIndex] = React.useState(0);

  const handleNavigateGroups = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Do nothing if the combobox is not open
      if (!open) return;
      // Do nothing if no tabs are passed, just a list of groups
      if (Array.isArray(groups)) return;
      // Do nothing if there is only one tab
      if (!Array.isArray(groups) && groups.tabs.length < 2) return;

      if (e.key === 'Tab') {
        e.preventDefault();
        setSelectedGroupIndex((prev) => (prev + 1) % groups.tabs.length);
      }
    },
    [open, groups],
  );

  const selectedGroups = (() => {
    if (Array.isArray(groups)) return groups;
    return groups.tabs[selectedGroupIndex]?.groups || [];
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {Trigger ? (
          Trigger
        ) : (
          <Button
            wobble={triggerProps?.wobble ?? true}
            variant={(selected || externalSelected) && highlightIfSelection ? 'default' : 'outline'}
            role="combobox"
            aria-expanded={open}
            className={cn('justify-between rounded-xl', triggerProps?.className)}
            invalid={triggerProps?.invalid}
          >
            {renderedValue}
            <div className="flex items-center">
              {controlled?.open?.shortcut && (
                <ShortcutKbd>{controlled?.open?.shortcut}</ShortcutKbd>
              )}
              <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className={cn('p-0', className)} side={side} align={align}>
        <Command className="rounded-2xl" loop={loop}>
          <CommandInput
            placeholder={placeholder}
            className="h-9"
            value={controlled?.search?.search}
            onValueChange={controlled?.search?.setSearch}
            rightAddon={commandInputRightAddon}
            onKeyDown={handleNavigateGroups}
          />
          {'tabs' in groups && (
            <div className="flex shrink-0 items-center gap-2 p-2">
              <ShortcutKbd key="tab-shortcut">Tab</ShortcutKbd>
              {groups.tabs.map((tab, i) => (
                <Button
                  tabIndex={-1}
                  key={i}
                  variant={i === selectedGroupIndex ? 'default' : 'outline'}
                  size="xs"
                  className="flex-1 shrink-0"
                  onClick={() => setSelectedGroupIndex(i)}
                >
                  {tab.title}
                </Button>
              ))}
            </div>
          )}
          <CommandList {...commandListProps}>
            {showNothingFound && <CommandEmpty>{nothingFound}</CommandEmpty>}
            <AnimatePresence mode="wait">
              <motion.div
                key={`group-index-${selectedGroupIndex}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
              >
                {selectedGroups.map((group, i) => (
                  <React.Fragment key={i}>
                    <CommandGroup heading={group.title} forceMount={group.forceMount}>
                      {separatorBetweenGroups && i !== 0 && (
                        <div key={`separator-${i}`} className="flex-center px-2">
                          <Separator className="opacity-40" />
                        </div>
                      )}
                      {!!group.emptyContent && !group.items.length && (
                        <CommandItem key="empty-content-placeholder" disabled>
                          {group.emptyContent}
                        </CommandItem>
                      )}
                      {group.items.map((item) => (
                        <CommandItem
                          key={item.value}
                          value={item.value}
                          keywords={[
                            item.value,
                            ...(typeof item.label === 'string' ? item.label : []),
                            ...(group.title ? [group.title] : []),
                            ...(item.keywords ?? []),
                          ]}
                          onSelect={() => {
                            if (group.onSelect) group.onSelect(item.value);
                            else if (item.onSelect) item.onSelect(item.value);
                            else onSelect?.({ ...item, groupIndex: i });

                            if (!disableInternalState) {
                              if (isSelectedItem(item.value, i)) {
                                setSelected(null);
                              } else {
                                setSelected({ ...item, groupIndex: i });
                              }
                            }

                            if (closeOnSelect) setOpen(false);
                          }}
                          className="group/item"
                        >
                          <div className="flex flex-col flex-1 gap-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 [&_svg]:size-4">
                                {item.leftElement && <>{item.leftElement}</>}
                                {typeof item.label === 'string' ? (
                                  <p>{item.label}</p>
                                ) : (
                                  <>{item.label}</>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {item.rightElementOnFocus && (
                                  <div className="hidden group-data-[selected=true]/item:block">
                                    {item.rightElementOnFocus}
                                  </div>
                                )}
                                {(item.isSelected || isSelectedItem(item.value, i)) && (
                                  <div className={cn('size-2 rounded-full bg-foreground')} />
                                )}
                              </div>
                            </div>
                            {item.description && (
                              <p className="text-muted-foreground text-sm whitespace-pre-wrap break-words">
                                {item.description}
                              </p>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </React.Fragment>
                ))}
              </motion.div>
            </AnimatePresence>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export {
  type Group as ComboboxGroup,
  type Item as ComboboxGroupItem,
  type SelectedItem as ComboboxSelectedItem,
};
